export class ApiErro extends Error {
    constructor (status, mensagem) {
        super(mensagem ?? `Falha na API (${status}).`);
        this.name = 'ApiErro';
        this.status = status;
    }
}

/**
 * Cliente REST autenticado do tablet: prefixa `/api/v1`, envia o token da
 * sessão e traduz resposta não-2xx em `ApiErro`.
 */
export class ApiClient {
    /**
     * @param {string} baseUrl ex.: "http://localhost:8000"
     * @param {{obterToken: () => (string|null), buscar?: typeof fetch}} opcoes
     */
    constructor (baseUrl, { obterToken, buscar = globalThis.fetch?.bind(globalThis), aoNaoAutorizado = null }) {
        this.baseUrl = `${String(baseUrl).replace(/\/+$/, '')}/api/v1`;
        this._obterToken = obterToken;
        this._buscar = buscar;
        // `401`: a sessão morreu no servidor (expirou ou foi substituída por outro login).
        this._aoNaoAutorizado = aoNaoAutorizado;
    }

    async requisitar (metodo, caminho, corpo) {
        const token = this._obterToken();
        const resposta = await this._buscar(`${this.baseUrl}${caminho}`, {
            method: metodo,
            headers: {
                ...(corpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {})
        });

        return this._ler(resposta);
    }

    /**
     * POST com corpo JSON comprimido. O backend compara `Content-Encoding`
     * por igualdade exata com "gzip" (docs/scan-pixel-integracao.md §5.1).
     */
    async enviarGzip (caminho, corpo, cabecalhos = {}) {
        const token = this._obterToken();
        const fluxo = new Blob([JSON.stringify(corpo)]).stream().pipeThrough(new CompressionStream('gzip'));
        const resposta = await this._buscar(`${this.baseUrl}${caminho}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Content-Encoding': 'gzip',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...cabecalhos
            },
            body: await new Response(fluxo).arrayBuffer()
        });
        return this._ler(resposta);
    }

    /** GET de um arquivo (PDF de material): devolve os bytes; o erro segue o mesmo envelope. */
    async baixar (caminho) {
        const token = this._obterToken();
        const resposta = await this._buscar(`${this.baseUrl}${caminho}`, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!resposta.ok) return this._ler(resposta);
        return resposta.arrayBuffer();
    }

    async _ler (resposta) {
        if (!resposta.ok) {
            const detalhe = await resposta.json().catch(() => ({}));
            const texto = typeof detalhe.detail === 'string' ? detalhe.detail : undefined;
            const erro = new ApiErro(resposta.status, detalhe.mensagem ?? texto);
            erro.codigo = detalhe.codigo ?? null;
            if (resposta.status === 401) this._aoNaoAutorizado?.(erro);
            throw erro;
        }
        return resposta.status === 204 ? null : resposta.json();
    }
}
